import { DEFAULT_LOCALE, isLocale, type Locale } from '@gr/core/platform/user-preferences/locales';
import type { KeyValueStorage } from '../storage';
import { Observable } from '../store';

// PREF-UC-1: локализация без библиотек. Словари — вложенные объекты строк,
// ключи — пути через точку. Если перевода нет, берётся эталонный русский
// (PREF-UC-1.CC-1), если нет и его — сам ключ (видно в интерфейсе и ловится тестом).

export interface Dictionary {
  [key: string]: string | Dictionary;
}

export type Params = Record<string, string | number>;

const STORAGE_KEY = 'gr.locale';

function lookup(dictionary: Dictionary | undefined, key: string): string | undefined {
  let node: string | Dictionary | undefined = dictionary;
  for (const part of key.split('.')) {
    if (node === undefined || typeof node === 'string') return undefined;
    node = node[part];
  }
  return typeof node === 'string' ? node : undefined;
}

function interpolate(template: string, params?: Params): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => (name in params ? String(params[name]) : match));
}

export class I18nService {
  readonly locale: Observable<Locale>;
  private readonly pluralRules = new Map<Locale, Intl.PluralRules>();

  constructor(
    private readonly dictionaries: Readonly<Record<Locale, Dictionary>>,
    private readonly storage: KeyValueStorage,
  ) {
    const saved = storage.get(STORAGE_KEY);
    this.locale = new Observable<Locale>(isLocale(saved) ? saved : DEFAULT_LOCALE);
  }

  setLocale(locale: Locale): void {
    this.storage.set(STORAGE_KEY, locale);
    this.locale.set(locale);
    if (typeof document !== 'undefined') document.documentElement.lang = locale;
  }

  has(key: string): boolean {
    return lookup(this.dictionaries[DEFAULT_LOCALE], key) !== undefined;
  }

  t = (key: string, params?: Params): string => {
    const text = lookup(this.dictionaries[this.locale.get()], key) ?? lookup(this.dictionaries[DEFAULT_LOCALE], key) ?? key;
    return interpolate(text, params);
  };

  /** Множественное число: ключ указывает на объект { one, few, many, other }. */
  tp = (key: string, count: number, params?: Params): string => {
    const locale = this.locale.get();
    let rules = this.pluralRules.get(locale);
    if (!rules) {
      rules = new Intl.PluralRules(locale);
      this.pluralRules.set(locale, rules);
    }
    const form = rules.select(count);
    const text = this.has(`${key}.${form}`) ? `${key}.${form}` : `${key}.other`;
    return this.t(text, { n: count, ...params });
  };
}
